import { Component, inject, Input, OnDestroy } from '@angular/core';
import { BaseComponent } from '../../base/base.component';
import { Subject, takeUntil } from 'rxjs';
import { SearchConfig, ApiEnvNames, API_STATUS, ExportConfig, ERROR_RESPONSE } from '../../../model/api.model';
import { FIELD_TYPES, FieldOption, FieldSelectedValue } from '../../../model/form.model';
import { TenureAreaOptions, TenureExportSearchConfig } from '../../../model/tenure.model';
import { CommonService } from '../../../services/common.service';
import { EXCEL_EXTENSION } from '../../../services/excel-export.service';
import { PaginatorService } from '../../../services/paginator-service.service';
import { CommonUtils } from '../../../utils/common.utils';
import { FieldNames } from '../../../model/form-fields.model';
import { TTENTenureTitle } from '../../../model/tenure/tten-tenure.model';
import { LexusGrantTenureTitle } from '../../../model/tenure/lexus-grant-tenure.model';

@Component({
  selector: 'app-tenure-base',
  standalone: true,
  imports: [],
  providers: [PaginatorService],
  templateUrl: './tenure-base.component.html',
  styleUrl: './tenure-base.component.scss'
})
export abstract class TenureBaseComponent extends BaseComponent implements OnDestroy {

  @Input() set options(value: any) {
    if (this.data === TTENTenureTitle) {
      this.handleToyotaRegions(value);
      this.handleTTENDealers(value);
    } else if (this.data === LexusGrantTenureTitle) {
      this.handleToyotaRegions(value);
    } else {
      this.handleToyotaRegions(value);
      this.handleDealerOptions(value);
    }
    this.handleJobs(value);
  };

  private handleToyotaRegions(value: any): void {
      if (value['toyotaRegions']) {
        const areaRegionDropDown = this.filterSchema.FieldsArray.find(x => x.fieldName === FieldNames['Area']);
        const BrandDropdown = this.filterSchema.FieldsArray.find(x=>x.fieldName === FieldNames['Brand'])
        if (areaRegionDropDown) {
          this.regionOption = value['toyotaRegions'];
          const isTenureAreas = this.data === TTENTenureTitle || this.data === LexusGrantTenureTitle;
          areaRegionDropDown['options'] = isTenureAreas ? this.regionOption : [...TenureAreaOptions, ...this.regionOption];
          if(BrandDropdown){
            if(BrandDropdown['selectedValue'] === 'LEXUS'){
              areaRegionDropDown['options'] = TenureAreaOptions
            }else if(BrandDropdown['selectedValue'] === 'TOYOTA'){
              areaRegionDropDown['options'] = this.regionOption
            }else{
              areaRegionDropDown['options'] = [...TenureAreaOptions, ...this.regionOption];
            }
          }
          areaRegionDropDown['isLoading'] = false;
      }
    }
  }

  private handleTTENDealers(value: any): void {
      if (value['ttenDealers']) {
        const ttenDealerOptions = this.filterSchema.FieldsArray.find(x => x.fieldName === FieldNames['DealerCode'])
        if (ttenDealerOptions) {
          this.ttenDealerOptions = value['ttenDealers']
          ttenDealerOptions['options'] = this.ttenDealerOptions;
          ttenDealerOptions['isLoading'] = false
        }
      }
  }

  private handleDealerOptions(value: any): void {
      if (value['dealerOptions'] && this.data !== TTENTenureTitle) {
        const dealerOptions = this.filterSchema.FieldsArray.find(x => x.fieldName === FieldNames['DealerCode'])
        if (dealerOptions) {
          this.dealerOptions = value['dealerOptions']
          dealerOptions['options'] = this.dealerOptions;
          dealerOptions['isLoading'] = false
        }
      }
    }

  private handleJobs(value: any): void {
    if (value['jobs']) {
      const jobRolesOptions = this.filterSchema.FieldsArray.find(x => x.fieldName ==='jobDescription')
      if (jobRolesOptions) {
        this.jobTitleOptions = value['jobs']
        jobRolesOptions['options'] = this.jobTitleOptions;
        jobRolesOptions['isLoading'] = false
      }
    }
  };
  
  protected abstract searchConfig: SearchConfig;

  protected tableFilter = '';
  protected currentPage: any;
  protected isFirstLoad = true;
  protected isDataInitialized = false;
  protected searchApiCalled = false;

  protected readonly destroy$ = new Subject<void>();
  protected readonly service = inject(CommonService);
  protected readonly commonUtils = inject(CommonUtils);
  protected readonly paginatorService = inject(PaginatorService);

  public regionOption:Array<FieldOption> = [];
  protected dealerOptions:Array<FieldOption> = [];
  protected jobTitleOptions:Array<FieldOption> = [];
  protected ttenDealerOptions:Array<FieldOption> = [];

  override ngOnInit() {
    super.ngOnInit();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback;
    this.dataTableSchema.exportCallback = this.exportCallback;
    this.dataTableSchema.sortCallback = this.sortCallback;
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] = this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      } else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
  }
  exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true;
    this.fetchAllData(searchConfig, true);
  };
  paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
    if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };
  sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {}
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction']
      })
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable()
        this.fetchAllData(searchConfig);
      }
    }
  }
  getSearchConfig = () => {
    const searchConfig = structuredClone(this.searchConfig);
    this.filterSchema['FieldsArray'].forEach((field) => {
      if (field['fieldCriteriaConfig']) {
        const operatorKey = `${field['fieldName']}Condition`;
        searchConfig['body'][operatorKey] = field['fieldCriteriaConfig']['value']
      }
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        if (Number.isNaN(field['selectedValue']) || Array.isArray(field['selectedValue'])) {
          searchConfig['body'][field['fieldName']] = field['selectedValue'];
        } else {
          searchConfig['body'][field['fieldName']] = field['selectedValue'].toString();
        }
      } else if (field['fieldType'] === FIELD_TYPES.NUMBER) {
        searchConfig['body'][field['fieldName']] = '';
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if(this.sortBy && Object.keys(this.sortBy).length > 0 ){
      if(!searchConfig['body']){
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig;
  };

  fetchAllData = (searchParam: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    if (callFromExport) {
      // Export mode: no pagination params needed
    } else {
      searchParam['body'].page = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchParam['body'].pageSize = Number(this.currentPage?.pageSize);
    }
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['Tenure']).then(async (response) => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result)) {
        if (callFromExport) {
          const exportConfig: ExportConfig = {
            tableSchema: this.dataTableSchema,
            environment: ApiEnvNames['StaffMaster'],
            fileExtension: EXCEL_EXTENSION,
            searchParameters: TenureExportSearchConfig,
            exportFileName: 'Tenure',
          };
          await this.service.downloadExcelFromS3(response.result[0], exportConfig)
        } else {
           this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;
           this.dataTableSchema.dataSource = this.handleResponse(response.result);
           if (!this.isDataInitialized && response.result.length > 0) {
              this.isDataInitialized = true;
           }
          this.props.updatedOn = response?.result[0] ? response.result[0].createdOn : '';
        }
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);        
      }
    }).catch((err) => {
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
    }).finally(() => {
      this.setTableLoadingState(false);
    });

  };
  override onSearchClicked = (searchParam: any) => {
    this.searchApiCalled = true
    if (searchParam) {
      this.resetSorting()
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig();    
    this.fetchAllData(searchConfig);
  };
  override onControlValueChanged = (event: FieldSelectedValue) => {
     const field = this.filterSchema.FieldsArray.find((p) => p.fieldName === event.FieldName);
     if (field) {
       field.selectedValue = event.Value;
     }
     if(event['FieldName'] === FieldNames['Brand']){
      const areaRegion = this.filterSchema.FieldsArray.find(x=> x.fieldName === FieldNames['Area'])
      if(areaRegion){
        areaRegion['options'] = [];
        areaRegion['selectedValue']='';
        if(event['Value'] === 'LEXUS'){
          areaRegion['displayName'] = 'Area';
          areaRegion['options'] = TenureAreaOptions
        }else if(event['Value'] === 'TOYOTA'){
          areaRegion['displayName'] = 'Region';
          if(this.regionOption){
          areaRegion['options'] = this.regionOption
          }
        }else{          
          areaRegion['displayName'] = 'Area / Region'
          areaRegion['options'] = [...TenureAreaOptions, ...structuredClone(this.regionOption)]
        }
      }
     }
     this.checkForAnyEmptyFields();
  }
  
  mapFieldValuesForDropDowns = (arr: any, field: FieldOption) => {
    const labelKey = field.fieldLabel || 'label';
    const valueKey = field.fieldValue || 'value';
    return arr.map((item: any) => ({
      fieldLabel: item[labelKey],
      fieldValue: item[valueKey]
    }));
  }

  override onResetClicked = () => {
     const areaRegion = this.filterSchema.FieldsArray.find(x=> x.fieldName === FieldNames['Area']);
     if(areaRegion){
     areaRegion['displayName'] = 'Area / Region'
     areaRegion['selectedValue']='';
     areaRegion['options'] = [...TenureAreaOptions, ...structuredClone(this.regionOption)]
     }
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] ? field['defaultValue'] : '' }));
    this.resetDataTable();
    this.resetSorting()
    this.dataTableSchema.inputFilter = '';
    this.checkForAnyEmptyFields(); 
  }
  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = { page: 0, pageSize: this.currentPage?.pageSize || this.dataTableSchema?.pageSizeOptions.pageSize[0] };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }
  
  ngOnDestroy(){
    this.destroy$.next();
    this.destroy$.complete();
  }
  handleResponse(dataArr:any){
    return dataArr
  }
}
